/**
 * Language Metadata
 * Names, locales and inbound tag aliases for the languages the platform serves.
 * @module @infra/i18n
 */

import type { Language, LanguageInfo } from './i18n.types';

/**
 * Language metadata for all supported languages.
 *
 * Every `locale` carries a region subtag, uniformly. `Intl` would infer the same region
 * from CLDR's likely-subtags if it were omitted — measured: dates, times, numbers,
 * first-day-of-week and sort order come out identical either way for all six. The subtag
 * is written out anyway so the table states the region rather than leaving it to be
 * looked up, and so no single language becomes the odd one out.
 *
 * `code` and `locale` are different fields on purpose: `code` is what travels on the wire
 * and lives in the database, `locale` is only ever handed to `Intl`, `<html lang>` and the
 * identity provider. Do not use one where the other belongs.
 */
export const LANGUAGE_INFO: Record<Language, LanguageInfo> = {
  vi: {
    code: 'vi',
    name: 'Vietnamese',
    nativeName: 'Tiếng Việt',
    locale: 'vi-VN',
    badge: 'vi',
  },
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    locale: 'en-US',
    badge: 'en',
  },
  ms: {
    code: 'ms',
    name: 'Malay',
    nativeName: 'Bahasa Malaysia',
    locale: 'ms-MY',
    badge: 'ms',
  },
  zh: {
    code: 'zh',
    name: 'Cantonese',
    nativeName: '廣東話',
    // `zh-HK`, not bare `zh`: CLDR maximizes a script-less `zh` to Simplified/mainland
    // China (`zh-Hans-CN`), while this platform serves Traditional/Hong Kong. Measured —
    // dropping `-HK` gets the wrong script, not just an unstated region.
    locale: 'zh-HK',
    badge: 'hk',
  },
  km: {
    code: 'km',
    name: 'Khmer',
    nativeName: 'ភាសាខ្មែរ',
    locale: 'km-KH',
    badge: 'km',
  },
  my: {
    code: 'my',
    name: 'Burmese',
    nativeName: 'မြန်မာဘာသာ',
    locale: 'my-MM',
    // `mm` (the country), not `my` (the language code): `my` reads as Malaysia's country
    // code, and Malay sits two rows up in the same picker.
    badge: 'mm',
  },
};

/**
 * Language tags a browser or partner may send that mean one of our languages.
 *
 * Only tags with no subtag to drop belong here. Everything hyphenated already narrows
 * to `zh` on its own via the loop below — `zh-HK`, `zh-Hant-HK` and `zh-Hant` all reach
 * the bare `zh` primary subtag by the time the last dash is stripped, and `zh` is now a
 * supported code in its own right, so no entry is needed for any of them.
 *
 * `yue` is not a tag a browser sends: it is the AI service's own code for Cantonese,
 * accepted here so a value coming back from that side still resolves. It is produced
 * only where a request leaves for the AI — `toAiLanguage` in
 * `@services/ai/realtime/ai-language.ts` for the roleplay socket, and its counterpart in
 * sales-coach-core for Core's HTTP calls.
 *
 * `khm`, `mya` and `bur` are ISO 639-2/B, which neither a browser nor the AI service
 * emits. They cost nothing and cover a partner that sends them; drop them if that never
 * materialises rather than leaving them to imply someone upstream relies on them.
 *
 * Known trade-off: the platform serves one Chinese — Traditional, for Hong Kong — under
 * the plain code `zh`. So `zh-CN`, a reader who expects Simplified, narrows to `zh` and
 * is served Traditional. Scoping the code to its region (`zh-hk`) would avoid that but
 * give Cantonese a code shaped unlike every other language's; the uniform shape won. If
 * Simplified is ever served too, this is the line that has to change first.
 *
 * Mirrors `LANGUAGE_ALIASES` in sales-coach-core (`src/common/language`). The two
 * runtimes cannot share the table; change them together.
 */
const LANGUAGE_ALIASES: Record<string, Language> = {
  yue: 'zh',
  khm: 'km',
  mya: 'my',
  bur: 'my',
};

/**
 * Resolve a raw language tag (e.g. `navigator.language`) to a supported code.
 *
 * Region subtags are dropped one at a time from the right rather than all at once, so
 * `en-US` still narrows to `en` while `zh-Hant-HK` reaches `zh` once its region and
 * script subtags are stripped, instead of failing to match on the full tag.
 */
export function normalizeLanguageTag(tag: string): Language | undefined {
  let candidate = tag.trim().toLowerCase();

  while (candidate) {
    if (isLanguageSupported(candidate)) return candidate;
    const alias = LANGUAGE_ALIASES[candidate];
    if (alias) return alias;

    const lastDash = candidate.lastIndexOf('-');
    if (lastDash <= 0) return undefined;
    candidate = candidate.slice(0, lastDash);
  }

  return undefined;
}

/**
 * Get language info
 */
export function getLanguageInfo(language: Language): LanguageInfo {
  return LANGUAGE_INFO[language];
}

/**
 * BCP 47 tag to hand to `Intl` / `toLocaleDateString` for a UI language. Callers must go
 * through this instead of inlining tags, so a new language formats dates in its own locale
 * instead of silently falling into whatever the `else` branch happened to be.
 */
export function getLanguageLocale(language: string): string {
  return isLanguageSupported(language) ? LANGUAGE_INFO[language].locale : LANGUAGE_INFO.en.locale;
}

/**
 * Get all available languages
 */
export function getAvailableLanguages(): LanguageInfo[] {
  return Object.values(LANGUAGE_INFO);
}

/**
 * Check if language is supported
 * Derived from LANGUAGE_INFO so adding a new language is a single-source change.
 */
export function isLanguageSupported(language: string): language is Language {
  return Object.prototype.hasOwnProperty.call(LANGUAGE_INFO, language);
}
